import type { CheckInRow } from "@andrum/database";
import type { ExperimentOutcome } from "@andrum/schemas";
import type { ToolboxItem } from "./derive-insights";
import { t } from "@/i18n";

export type InsightStage = "empty" | "early" | "building" | "rich";

export type MainInsight = {
  stage: InsightStage;
  /** One-line value statement */
  headline: string;
  /** Supporting detail */
  body: string;
  /** Soft next step */
  nextStep: string;
  signals: Array<{ key: string; label: string; value: string }>;
};

function avg(nums: number[]): number | null {
  if (!nums.length) return null;
  return Math.round((nums.reduce((a, b) => a + b, 0) / nums.length) * 10) / 10;
}

function energyTrend(checkIns: CheckInRow[]): "up" | "down" | "flat" | null {
  const withE = checkIns
    .filter((c) => c.energy != null)
    .slice(0, 14)
    .map((c) => c.energy as number);
  if (withE.length < 4) return null;
  const mid = Math.floor(withE.length / 2);
  const recent = avg(withE.slice(0, mid));
  const older = avg(withE.slice(mid));
  if (recent == null || older == null) return null;
  const d = recent - older;
  if (d >= 0.6) return "up";
  if (d <= -0.6) return "down";
  return "flat";
}

/**
 * Build the single most useful insight for the user given how much data they have.
 */
export function buildMainInsight(input: {
  checkIns: CheckInRow[];
  outcomes: ExperimentOutcome[];
  toolbox: ToolboxItem[];
  avgEnergy: number | null;
  avgStress: number | null;
}): MainInsight {
  const checkInCount = input.checkIns.length;
  const completed = input.outcomes.filter((o) => o.completed).length;
  const top = input.toolbox[0] ?? null;
  const topCategory = top?.category ?? null;
  const trend = energyTrend(input.checkIns);

  const signals: MainInsight["signals"] = [
    {
      key: "checkins",
      label: t("insights.checkIns"),
      value: String(checkInCount),
    },
    {
      key: "experiments",
      label: t("insights.experiments"),
      value: String(completed),
    },
  ];
  if (input.avgEnergy != null) {
    signals.push({
      key: "energy",
      label: t("insights.avgEnergy"),
      value: `${input.avgEnergy}`,
    });
  }
  if (input.avgStress != null) {
    signals.push({
      key: "stress",
      label: t("insights.avgStress"),
      value: `${input.avgStress}`,
    });
  }

  // Empty: almost no data
  if (checkInCount === 0 && completed === 0) {
    return {
      stage: "empty",
      headline: t("insights.mainEmptyHeadline"),
      body: t("insights.mainEmptyBody"),
      nextStep: t("insights.mainEmptyNext"),
      signals,
    };
  }

  // Early: few data points — encourage map building
  if (checkInCount < 4 && completed < 2) {
    return {
      stage: "early",
      headline: t("insights.mainEarlyHeadline", {
        checkIns: checkInCount,
        experiments: completed,
      }),
      body: t("insights.mainEarlyBody"),
      nextStep: t("insights.mainEarlyNext"),
      signals,
    };
  }

  // Rich: clear personal pattern
  if ((completed >= 5 || checkInCount >= 10) && top) {
    const liked =
      top.avgRepeatIntent != null && top.avgRepeatIntent >= 1.5
        ? t("insights.mainRichLiked", { title: top.title })
        : t("insights.mainRichTop", {
            title: top.title,
            count: top.timesCompleted,
          });

    let trendLine = "";
    if (trend === "up") trendLine = t("insights.mainTrendUp");
    else if (trend === "down") trendLine = t("insights.mainTrendDown");
    else if (trend === "flat") trendLine = t("insights.mainTrendFlat");

    return {
      stage: "rich",
      headline: liked,
      body: [trendLine, topCategory ? t("insights.mainCategoryHint", { category: topCategory }) : ""]
        .filter(Boolean)
        .join(" "),
      nextStep: t("insights.mainRichNext", { title: top.title }),
      signals,
    };
  }

  // Building: some data, emerging signal
  if (top) {
    return {
      stage: "building",
      headline: t("insights.mainBuildingHeadline", { title: top.title }),
      body: t("insights.mainBuildingBody", {
        checkIns: checkInCount,
        experiments: completed,
      }),
      nextStep: t("insights.mainBuildingNext"),
      signals,
    };
  }

  return {
    stage: "building",
    headline: t("insights.mainBuildingGenericHeadline"),
    body: t("insights.mainBuildingGenericBody", { checkIns: checkInCount }),
    nextStep: t("insights.mainEarlyNext"),
    signals,
  };
}
